import { ChevronsDown, Play } from 'lucide-react';
import { Reveal, Chapter } from './Reveal';
import { GoldButton } from './GoldButton';
import { CHECKOUT_LINKS } from '../../config/site';
import { trackClick } from '../../lib/track';

const MODULES = [
  { title: 'Capa e Índice', img: '/assets/eagle-modules.webp', pos: '50% 22%' },
  { title: 'A Gaiola Invisível', img: '/assets/hero-eagle.webp', pos: '62% 18%' },
  { title: 'O Despertar', img: '/assets/eagle-modules.webp', pos: '50% 55%' },
  { title: 'Eliminando os Corvos', img: '/assets/hero-eagle.webp', pos: '40% 30%' },
  { title: 'As Asas da Disciplina', img: '/assets/eagle-modules.webp', pos: '50% 78%' },
  { title: 'Voo Profundo', img: '/assets/hero-eagle.webp', pos: '55% 45%' },
  { title: 'Os 28 Dias do Despertar', img: '/assets/eagle-modules.webp', pos: '50% 92%' },
];

const ModuleCard = ({ n, title, img, pos }) => (
  <div data-testid={`module-card-${n}`} className="group relative h-32 overflow-hidden rounded-2xl border border-white/[0.08]">
    <img
      src={img}
      alt={`Módulo ${n} — ${title}`}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      style={{ objectPosition: pos }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/25" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#22C55E] shadow-[0_0_25px_rgba(34,197,94,0.55)] transition-transform duration-300 group-hover:scale-110">
        <Play className="ml-0.5 h-4 w-4 fill-white text-white" />
      </span>
    </div>
    <div className="absolute inset-x-0 bottom-0 p-3 text-center">
      <span className="font-accent text-[8px] font-bold tracking-[0.3em] text-gold-light/80">MÓDULO 0{n}</span>
      <p className="font-cinzel text-gold text-[13px] font-semibold uppercase leading-tight tracking-[0.08em]">{title}</p>
    </div>
  </div>
);

export const TrainingSection = () => (
  <section data-testid="training-section" className="relative border-t border-white/5 bg-[#060606]">
    <div className="mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-36">
      <Chapter index="02" label="Treinamento Complementar" />

      <div className="grid items-center gap-16 lg:grid-cols-2">
        <Reveal className="order-2 lg:order-1">
          <div className="relative mx-auto w-fit">
            <div
              data-testid="training-phone-frame"
              className="relative rounded-[3rem] bg-gradient-to-b from-white/25 via-white/10 to-white/5 p-[2px] shadow-[0_40px_100px_rgba(0,0,0,0.8)]"
            >
              <div className="rounded-[2.9rem] bg-black p-2.5">
                <div className="relative h-[540px] w-[270px] overflow-hidden rounded-[2.2rem] bg-[#0a0a0a] sm:h-[580px] sm:w-[290px]">
                  <div className="absolute left-1/2 top-2.5 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
                  <div className="phone-scroll h-full w-full overflow-y-auto px-3.5 pb-8 pt-12">
                    <div className="mb-5 px-1">
                      <p className="font-cinzel text-[15px] font-bold uppercase tracking-[0.12em] text-white">Despertar da Águia</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="rounded-md bg-white/[0.07] px-2 py-0.5 font-accent text-[9px] font-medium tracking-[0.15em] text-neutral-300">7 Módulos</span>
                        <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                          <div className="h-full w-[18%] rounded-full bg-gold" />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3.5">
                      {MODULES.map((m, i) => (
                        <ModuleCard key={m.title} n={i + 1} {...m} />
                      ))}
                    </div>
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-black to-transparent" />
                </div>
              </div>
            </div>

            <div className="absolute -right-4 top-10 z-10 rounded-full bg-gold px-4 py-2 font-accent text-[11px] font-bold tracking-[0.2em] text-black shadow-[0_0_35px_rgba(212,175,55,0.45)] sm:-right-8">
              7 MÓDULOS
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-neutral-500">
              <ChevronsDown className="h-4 w-4 animate-bounce text-gold/60" />
              <span className="font-accent text-[10px] tracking-[0.3em] uppercase">Role dentro da tela</span>
            </div>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              7 módulos para
              <span className="text-gold"> acelerar sua evolução.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-neutral-400">
              O treinamento complementar aprofunda o que o aplicativo pratica: aulas diretas ao ponto,
              em vídeo, para você assistir no seu ritmo e aplicar no mesmo dia.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <ul className="mt-8 space-y-4">
              {[
                'Aulas em vídeo curtas e objetivas',
                'Acesso imediato após o pagamento',
                'Assista quando e onde quiser, para sempre',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-[15px] text-neutral-300">
                  <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.25} className="mt-12">
            <GoldButton
              testId="training-cta-button"
              href={CHECKOUT_LINKS.training}
              onClick={() => trackClick('treinamento-quero-acessar', 'treinamento')}
            >
              Quero acessar o conteúdo
            </GoldButton>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
